import { Node, mergeAttributes } from '@tiptap/core';

/**
 * StepFlow: a short ordered sequence where the ORDER is the teaching.
 *
 * Built for James 1:19 in Biblical Marriage session 2 ("quick to hear, then
 * slow to speak, then slow to anger"), where the sequence was doing real work
 * inside a sentence and reading as prose. Deliberately generic rather than
 * scripture-specific: the same shape fits any 2-5 step progression, and other
 * courses will want it.
 *
 * Attrs:
 *  - steps: JSON array of { label, detail? }. Stored as an attribute string so
 *    the node stays an atom and the text never becomes separately editable
 *    prose that could drift from the passage it quotes.
 *  - variant: 'arrow' (default) shows connectors; 'numbered' shows 1..n.
 */
/**
 * Marks a step can carry. Kept small and generic so the block stays reusable:
 * a sequence about listening wants an ear, one about a process wants none.
 */
const STEP_ICONS = {
  ear: '<path d="M6 8.5a6 6 0 0 1 12 0c0 3-2 4-3 5.5-.8 1.2-.5 3-2.5 3.5"/><path d="M9 9a3 3 0 0 1 6 0"/><path d="M8.5 19.5c-1 1-2.5 1-3.5 0"/>',
  speak: '<path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8Z"/><path d="M8.5 12h7"/>',
  flame: '<path d="M12 22c4 0 6.5-2.6 6.5-6 0-4.2-4-6.2-4-10 0 0-2.2 1.2-2.2 4.2 0 1.6-1 2.3-1.8 2.3-1 0-1.7-.8-1.7-2 0 0-2.3 2.1-2.3 5.5 0 3.4 2.5 6 5.5 6Z"/>',
  eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>',
  shield: '<path d="M12 22s8-3.5 8-10V5.5l-8-3-8 3V12c0 6.5 8 10 8 10Z"/>',
  anchor: '<circle cx="12" cy="5" r="2.5"/><path d="M12 7.5V22"/><path d="M5 13a7 7 0 0 0 14 0"/><path d="M3 13h4M17 13h4"/>',
  book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/><path d="M9 7h7M9 11h5"/>',
  pray: '<path d="M12 3c-1.1 2.6-1.6 5-1.6 7.2 0 1.6.4 2.6 1.6 3.8 1.2-1.2 1.6-2.2 1.6-3.8C13.6 8 13.1 5.6 12 3Z"/><path d="M8.5 21c.8-2.2 2-3.4 3.5-3.4S14.7 18.8 15.5 21"/><path d="M5 21h14"/>',
  hand: '<path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12"/><path d="M11 12V4.5a1.5 1.5 0 0 1 3 0V12"/><path d="M14 12V6.5a1.5 1.5 0 0 1 3 0V13"/><path d="M17 11.5a1.5 1.5 0 0 1 3 0V15a7 7 0 0 1-7 7h-1a7 7 0 0 1-7-7v-3a1.5 1.5 0 0 1 3 0"/>',
};
const stepIconSvg = (key) =>
  STEP_ICONS[key]
    ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + STEP_ICONS[key] + '</svg>'
    : '';

const parseSteps = (raw) => {
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const StepFlow = Node.create({
  name: 'stepFlow',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      steps: {
        default: '[]',
        parseHTML: (element) => element.getAttribute('data-steps') || '[]',
        renderHTML: (attributes) => ({
          'data-steps':
            typeof attributes.steps === 'string'
              ? attributes.steps
              : JSON.stringify(attributes.steps || []),
        }),
      },
      variant: {
        default: 'arrow',
        parseHTML: (element) => element.getAttribute('data-variant') || 'arrow',
        renderHTML: (attributes) => ({ 'data-variant': attributes.variant || 'arrow' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="step-flow"]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const steps = parseSteps(node.attrs.steps);
    const variant = node.attrs.variant || 'arrow';
    const children = [];
    steps.forEach((step, i) => {
      if (i > 0) {
        children.push(['div', { class: 'step-flow-link', 'aria-hidden': 'true' }]);
      }
      children.push([
        'div',
        { class: 'step-flow-step' },
        ['div', { class: 'step-flow-marker' }, String(i + 1)],
        [
          'div',
          { class: 'step-flow-body' },
          ['div', { class: 'step-flow-label' }, step.label || ''],
          ...(step.detail ? [['div', { class: 'step-flow-detail' }, step.detail]] : []),
        ],
      ]);
    });
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'step-flow',
        class: `step-flow step-flow--${variant}`,
      }),
      ...children,
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = `step-flow step-flow--${node.attrs.variant || 'arrow'}`;
      dom.setAttribute('data-type', 'step-flow');
      dom.setAttribute('data-steps', typeof node.attrs.steps === 'string' ? node.attrs.steps : JSON.stringify(node.attrs.steps || []));
      parseSteps(node.attrs.steps).forEach((step, i) => {
        if (i > 0) {
          const link = document.createElement('div');
          link.className = 'step-flow-link';
          link.setAttribute('aria-hidden', 'true');
          dom.appendChild(link);
        }
        const el = document.createElement('div');
        el.className = 'step-flow-step';
        const marker = document.createElement('div');
        marker.className = 'step-flow-marker';
        // A step may carry its own marker text. HEAR is an acronym, so the
        // letters are the sequence; a 1-2-3-4 beside them says nothing.
        const markerText = step.marker || String(i + 1);
        if (step.icon && STEP_ICONS[step.icon]) {
          marker.classList.add('step-flow-marker--icon');
          marker.innerHTML = stepIconSvg(step.icon);
          const badge = document.createElement('span');
          badge.className = 'step-flow-num';
          badge.textContent = markerText;
          if (step.marker) badge.classList.add('step-flow-num--letter');
          marker.appendChild(badge);
        } else {
          marker.textContent = markerText;
        }
        const body = document.createElement('div');
        body.className = 'step-flow-body';
        const label = document.createElement('div');
        label.className = 'step-flow-label';
        label.textContent = step.label || '';
        body.appendChild(label);
        if (step.detail) {
          const detail = document.createElement('div');
          detail.className = 'step-flow-detail';
          detail.textContent = step.detail;
          body.appendChild(detail);
        }
        el.appendChild(marker);
        el.appendChild(body);
        dom.appendChild(el);
      });
      return { dom };
    };
  },
});

export default StepFlow;
